/** Small helpers over the category tree the settings and pickers share. */

import type { SdCategoryAdminNode } from '@/lib/serviceDeskTypes';

/** The chain from level 1 down to the node with this id, or [] when it is not in the tree. */
export function pathTo(tree: SdCategoryAdminNode[], id: number | null): SdCategoryAdminNode[] {
  if (id === null) return [];
  for (const node of tree) {
    if (node.id === id) return [node];
    const below = pathTo(node.children, id);
    if (below.length > 0) return [node, ...below];
  }
  return [];
}

export function findNode(tree: SdCategoryAdminNode[], id: number | null): SdCategoryAdminNode | null {
  const p = pathTo(tree, id);
  return p.length > 0 ? (p[p.length - 1] ?? null) : null;
}

/** A copy of the tree with inactive nodes removed (pickers never offer them). */
export function activeOnly(tree: SdCategoryAdminNode[]): SdCategoryAdminNode[] {
  return tree.filter((n) => n.is_active).map((n) => ({ ...n, children: activeOnly(n.children) }));
}

/** "Hardware > Laptop > Screen cracked" */
export function pathLabel(tree: SdCategoryAdminNode[], id: number | null): string {
  return pathTo(tree, id)
    .map((n) => n.name)
    .join(' > ');
}

/** Every node id in a subtree, the node itself first. */
export function subtreeIds(node: SdCategoryAdminNode): number[] {
  return [node.id, ...node.children.flatMap(subtreeIds)];
}

/** Every node that has children, so "Expand all" knows what to open. */
export function branchIds(tree: SdCategoryAdminNode[]): number[] {
  return tree.flatMap((n) => (n.children.length > 0 ? [n.id, ...branchIds(n.children)] : []));
}

/** The ids of every ancestor of a node, nearest parent last. */
export function ancestorIds(tree: SdCategoryAdminNode[], id: number | null): number[] {
  return pathTo(tree, id)
    .slice(0, -1)
    .map((n) => n.id);
}

export type TreeSearch = {
  /** Nodes to draw: a match, or something above a match. Null when no search is active. */
  visible: Set<number> | null;
  /** Nodes whose own name matches. */
  matches: Set<number>;
  /** Nodes to open so every match can be seen. */
  opened: Set<number>;
};

/**
 * Walks the tree for a search phrase. A node matches when its name contains the
 * text (any case). Matches, and the branches leading to them, stay visible and
 * the branches are opened; everything under a match stays listed (closed, so one
 * click shows it); everything else is hidden while the search is active.
 */
export function searchTree(tree: SdCategoryAdminNode[], text: string): TreeSearch {
  const needle = text.trim().toLowerCase();
  const matches = new Set<number>();
  if (!needle) return { visible: null, matches, opened: new Set() };
  const visible = new Set<number>();
  const opened = new Set<number>();
  // Returns whether anything in these nodes' subtrees matches.
  const walk = (nodes: SdCategoryAdminNode[], underMatch: boolean): boolean => {
    let any = false;
    for (const n of nodes) {
      const self = n.name.toLowerCase().includes(needle);
      const below = walk(n.children, underMatch || self);
      if (self) matches.add(n.id);
      if (self || below || underMatch) visible.add(n.id);
      if (below) opened.add(n.id);
      if (self || below) any = true;
    }
    return any;
  };
  walk(tree, false);
  return { visible, matches, opened };
}
